import {
  Box, Grid, IconButton, Paper, Popover, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import useLogError from '../../../hooks/useLogError';
import { ChangeEvent, FC, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import useNswagClient from '../../../hooks/api/useNswagClient';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import LoadingWheel from '../../../components/ui/LoadingWheel';
import { exportFile, formatDateString } from '../../../utils';
import { UserContext } from '../../../components/shared/useUser';
import { DotsVertical } from '../../../assets';
import { useSearchParams } from 'react-router-dom';
import { Loader } from '../../../components/ui/Loader';
import { ItemDailyHistory, RequestItemHistoryReportRequest } from '../../../app/services/api/generated';

type Props = {
  currencyCode?: string;
  primaryUom?: string;
}

const MAX_DATE_RANGE_DAYS = 180;

const History: FC<Props> = ({ currencyCode, primaryUom }) => {
  const { t } = useTranslation('productDetails');
  const [searchParams] = useSearchParams();
  const sku = searchParams.get('sku');
  const theme = useTheme();
  const { selectedStore, hasPermissionTo } = useContext(UserContext);
  const [hasPermissionToReadMadeInStore, setHasPermissionToReadMadeInStore] = useState<boolean>(false);
  const { logError } = useLogError();
  const { getItemDailyHistory, getItemHistoryReportById } = useNswagClient();
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [data, setData] = useState<ItemDailyHistory[]>([]);
  const [fromDate, setFromDate] = useState<string>(dayjs().subtract(7, 'day').format('YYYY-MM-DD'));
  const [toDate, setToDate] = useState<string>(dayjs().format('YYYY-MM-DD'));
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);

  useEffect(() => {
    if (selectedStore) {
      loadData(fromDate, toDate);
    }
    setHasPermissionToReadMadeInStore(hasPermissionTo(['MadeInStoreRead']));
  }, []);

  const loadData = (from: string, to: string) => {
    if (!from || !to) {
      return;
    }
    if (sku && selectedStore?.storeNumber) {
      setLoading(true);

      getItemDailyHistory(sku, selectedStore?.storeNumber, from, to)
        .then((result) => {
          setData(result.data ?? []);
        })
        .catch((error) => {
          logError(error);
        })
        .finally(() => setLoading(false));
    }
  };
  const getMaxDate = () => dayjs().format('YYYY-MM-DD');

  const getMinDate = () => '1900-01-01';

  const getMaxToDate = (from?: string, today = dayjs()) => {
    if (!from) {
      return today.format('YYYY-MM-DD');
    }

    const fromPlus31 = dayjs(from).add(MAX_DATE_RANGE_DAYS, 'day');

    return (fromPlus31.isBefore(today) ? fromPlus31 : today).format('YYYY-MM-DD');
  };

  const getMinFromDate = () => {
    if (!toDate) {
      return getMinDate();
    }

    return dayjs(toDate).subtract(MAX_DATE_RANGE_DAYS, 'day').format('YYYY-MM-DD');
  };

  const handleFromDateChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const value = event.target.value;

    if (!value) {
      return;
    }

    if (!toDate) {
      setFromDate(value);
      return;
    }

    const newFromDate = dayjs(value);
    const currentToDate = dayjs(toDate);

    if (newFromDate.isAfter(currentToDate)) {
      return;
    }

    if (currentToDate.diff(newFromDate, 'day') > MAX_DATE_RANGE_DAYS) {
      return;
    }

    setFromDate(value);
    loadData(value, toDate);
  };

  const handleToDateChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const value = event.target.value;

    if (!value) {
      return;
    }

    if (!fromDate) {
      setToDate(value);
      return;
    }
    const newToDate = dayjs(value);
    const currentFromDate = dayjs(fromDate);
    const today = dayjs();

    if (newToDate.isAfter(today)) {
      return;
    }

    if (newToDate.isBefore(currentFromDate)) {
      return;
    }

    if (newToDate.diff(currentFromDate, 'day') > MAX_DATE_RANGE_DAYS) {
      return;
    }

    setToDate(value);
    loadData(fromDate, value);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };

  const hasDisplayValue = (value?: number | null): value is number => (
    value !== undefined && value !== null && value !== 0 && !Number.isNaN(value)
  );

  const numericColumns = [
    'dayPrice',
    'opening',
    'receipts',
    'madeInStore',
    'transferIn',
    'transferOut',
    'waste',
    'returnToVendor',
    'giveaway',
    'sample',
    'theoUsage',
    'theoClosing',
    'inventory',
    'closingVar',
  ] as const;

  type NumericColumn = typeof numericColumns[number];

  const getDecimalPlaces = (value: number): number => {
    if (Number.isInteger(value)) {
      return 0;
    }

    const roundedOneDecimal = Number(value.toFixed(1));

    if (Math.abs(value - roundedOneDecimal) < 0.0000001) {
      return 1;
    }

    return 2;
  };

  const columnPrecisionMap = useMemo<Record<NumericColumn, number>>(() => {
    return numericColumns.reduce((accumulator, column) => {
      const precision = data.reduce((maxPrecision, row) => {
        const value = row[column];

        if (!hasDisplayValue(value)) {
          return maxPrecision;
        }

        return Math.max(maxPrecision, getDecimalPlaces(value));
      }, 0);

      accumulator[column] = precision;
      return accumulator;
    }, {} as Record<NumericColumn, number>);
  }, [data]);

  const appendPrimaryUom = (value: string): string => {
    if (value === '-' || !primaryUom) {
      return value;
    }

    return `${value} ${primaryUom}`;
  };

  const formatNumericDisplayValue = (column: NumericColumn, value?: number | null): string => {
    if (!hasDisplayValue(value)) {
      return '-';
    }

    const fractionDigits = columnPrecisionMap[column];

    if (column === 'dayPrice' && currencyCode) {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }).format(value);
    }

    if (column === 'dayPrice') {
      return value.toFixed(fractionDigits);
    }

    return appendPrimaryUom(value.toFixed(fractionDigits));
  };

  const renderNumericDisplayValue = (column: NumericColumn, value?: number | null): ReactNode => {
    const displayValue = formatNumericDisplayValue(column, value);

    if (displayValue !== '-') {
      return displayValue;
    }

    return (
      <Box component='span'
        sx={{ display: 'inline-block', width: '5ch', textAlign: 'center' }}>
        {displayValue}
      </Box>
    );
  };

  const getDayCode = (value?: string | null): string => {
    if (!value) {
      return '-';
    }

    const parsedDate = dayjs(value);

    return parsedDate.isValid() ? parsedDate.format('ddd') : '-';
  };

  const getDisplayDate = (value?: string | null): string => {
    if (!value) {
      return '-';
    }

    return formatDateString(value);
  };

  const onDownload = (): void => {
    if (sku) {
      setIsDownloading(true);
      const requestBody: RequestItemHistoryReportRequest = {
        itemId: sku,
        storeNumber: selectedStore?.storeNumber,
        from: fromDate,
        to: toDate,
      };

      getItemHistoryReportById(requestBody).then((response) => {
        if (response) {
          exportFile(response.data, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', `Item History Report - ${selectedStore?.storeNumber}.xlsx`);
        }
      }).catch((error) => {
        logError(error);
      }).finally(() => {
        setIsDownloading(false);
        handleClose();
      });
    }
  };

  return (
    <Paper sx={{ display: 'flex', flexDirection: 'column', borderRadius: '5px', width: '100%', height: '100%', my: 5, px: 5, pb: 5 }}>
      <Grid container
        py={5}>
        <Grid item
          xs={12}
          sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton aria-label={t('historyTab.downloadFullReport')}
            onClick={handleClick}
          >
            <DotsVertical />
          </IconButton>
          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <Box p={4}
              sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography
                variant="textSM"
                display='flex'
                flexDirection='row'
                alignItems='center'
                sx={{ p: 3, cursor: 'pointer', pointerEvents: isDownloading ? 'none' : '' }}
                onClick={onDownload}
              >
                {t('historyTab.downloadFullReport')}
                {isDownloading && <Loader />}
              </Typography>
            </Box>
          </Popover>
        </Grid>
        <Grid item
          md={3}
          xs={12}>
          <TextField
            label={t('historyTab.startDate')}
            type="date"
            required
            value={fromDate}
            onChange={handleFromDateChange}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              inputProps: {
                min: getMinFromDate(),
                max: toDate || getMaxDate(),
              },
            }}
            fullWidth
          />
        </Grid>

        <Grid item
          sx={{ ml: isMobile ? '' : 3, mt: isMobile ? 5 : '' }}
          md={3}
          xs={12}>
          <TextField
            label={t('historyTab.endDate')}
            type="date"
            required
            value={toDate}
            onChange={handleToDateChange}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              inputProps: {
                min: fromDate || getMinDate(),
                max: getMaxToDate(fromDate),
              },
            }}
            fullWidth
          />
        </Grid>

      </Grid>
      <Grid item
        mt={5}
        xs={12}>
        {
          loading ? <LoadingWheel /> : (
            <TableContainer>
              <Table>
                <TableHead
                  sx={{ paddingTop: '10px' }}>
                  <TableRow>
                    <Tooltip title={t('historyTab.dateTooltip')}>
                      <TableCell>
                        {t('historyTab.date')}
                      </TableCell>
                    </Tooltip>
                    <Tooltip title={t('historyTab.dayPriceTooltip')}>
                      <TableCell align='right'>
                        {t('historyTab.dayPrice')}
                      </TableCell>
                    </Tooltip>
                    <Tooltip title={t('historyTab.openingTooltip')}>
                      <TableCell align='right'>
                        {t('historyTab.opening')}
                      </TableCell>
                    </Tooltip>
                    <Tooltip title={t('historyTab.receiptsTooltip')}>
                      <TableCell align='right'>
                        {t('historyTab.receipts')}
                      </TableCell>
                    </Tooltip>
                    {hasPermissionToReadMadeInStore && <Tooltip title={t('historyTab.madeInStoreTooltip')}>
                      <TableCell align='right'>
                        {t('historyTab.madeInStore')}
                      </TableCell>
                    </Tooltip>}
                    <Tooltip title={t('historyTab.transferInTooltip')}>
                      <TableCell align='right'>
                        {t('historyTab.transferIn')}
                      </TableCell>
                    </Tooltip>
                    <Tooltip title={t('historyTab.transferOutTooltip')}>
                      <TableCell align='right'>
                        {t('historyTab.transferOut')}
                      </TableCell>
                    </Tooltip>
                    <Tooltip title={t('historyTab.wasteTooltip')}>
                      <TableCell align='right'>
                        {t('historyTab.waste')}
                      </TableCell>
                    </Tooltip>
                    <Tooltip title={t('historyTab.returnToStockTooltip')}>
                      <TableCell align='right'>
                        {t('historyTab.returnToStock')}
                      </TableCell>
                    </Tooltip>
                    <Tooltip title={t('historyTab.giveawayTooltip')}>
                      <TableCell align='right'>
                        {t('historyTab.giveaway')}
                      </TableCell>
                    </Tooltip>
                    <Tooltip title={t('historyTab.sampleTooltip')}>
                      <TableCell align='right'>
                        {t('historyTab.sample')}
                      </TableCell>
                    </Tooltip>
                    <Tooltip title={t('historyTab.usageTooltip')}>
                      <TableCell align='right'>
                        {t('historyTab.usage')}
                      </TableCell>
                    </Tooltip>
                    <Tooltip title={t('historyTab.theoClosingTooltip')}>
                      <TableCell align='right'>
                        {t('historyTab.theoClosing')}
                      </TableCell>
                    </Tooltip>
                    <Tooltip title={t('historyTab.inventoryTooltip')}>
                      <TableCell align='right'>
                        {t('historyTab.inventory')}
                      </TableCell>
                    </Tooltip>
                    <Tooltip title={t('historyTab.closingVarTooltip')}>
                      <TableCell align='right'>
                        {t('historyTab.closingVar')}
                      </TableCell>
                    </Tooltip>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {
                    data?.length === 0 ?
                      <TableRow>
                        <TableCell colSpan={11}>
                          <Typography variant="textSM">{t('noData')}</Typography>
                        </TableCell>
                      </TableRow> :
                      data?.map((item: ItemDailyHistory) => (
                        <TableRow key={item.date}>
                          <TableCell>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', minWidth: '120px' }}>
                              <span>{getDayCode(item.date)}</span>
                              <span>{getDisplayDate(item.date)}</span>
                            </Box>
                          </TableCell>
                          <TableCell align='right'>
                            {renderNumericDisplayValue('dayPrice', item?.dayPrice)}
                          </TableCell>
                          <TableCell align='right'>
                            {renderNumericDisplayValue('opening', item?.opening)}
                          </TableCell>
                          <TableCell align='right'>
                            {renderNumericDisplayValue('receipts', item?.receipts)}
                          </TableCell>
                          {hasPermissionToReadMadeInStore && <TableCell align='right'>
                            {renderNumericDisplayValue('madeInStore', item?.madeInStore)}
                          </TableCell>}
                          <TableCell align='right'>
                            {renderNumericDisplayValue('transferIn', item?.transferIn)}
                          </TableCell>
                          <TableCell align='right'>
                            {renderNumericDisplayValue('transferOut', item?.transferOut)}
                          </TableCell>
                          <TableCell align='right'>
                            {renderNumericDisplayValue('waste', item?.waste)}
                          </TableCell>
                          <TableCell align='right'>
                            {renderNumericDisplayValue('returnToVendor', item?.returnToVendor)}
                          </TableCell>
                          <TableCell align='right'>
                            {renderNumericDisplayValue('giveaway', item?.giveaway)}
                          </TableCell>
                          <TableCell align='right'>
                            {renderNumericDisplayValue('sample', item?.sample)}
                          </TableCell>
                          <TableCell align='right'>
                            {renderNumericDisplayValue('theoUsage', item?.theoUsage)}
                          </TableCell>
                          <TableCell align='right'>
                            {renderNumericDisplayValue('theoClosing', item?.theoClosing)}
                          </TableCell>
                          <TableCell align='right'>
                            {renderNumericDisplayValue('inventory', item?.inventory)}
                          </TableCell>
                          <TableCell align='right'>
                            {renderNumericDisplayValue('closingVar', item?.closingVar)}
                          </TableCell>
                        </TableRow>
                      ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        }
      </Grid>
    </Paper>
  );
};

export default History;
